import { useContext, useEffect, useMemo, useState, type FC } from "react";
import type { Dayjs } from "dayjs";
import { DownloadOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import AppContext from "../../context/AppContext";
import { AppStrings } from "../../utils/constants/AppStrings";
import { SIDER_TABS } from "../../utils/Constants";
import { useDebouncedValue } from "../../hooks/useDebouncedSearchQuery";
import { Button, DatePicker, PillTabs } from "../shared";
import { usePageTitle } from "../CommonComponents/Layout/PageTitleContext";

import styles from "./Affiliates.module.css";
import { mapKpiMetricsToRow, type AffiliateRow } from "./affiliatesData";
import AffiliateKpis from "./AffiliateKpis/AffiliateKpis";
import AffiliatesList from "./AffiliatesList/AffiliatesList";
import { useAffiliatesList } from "../../queries/affiliates/useAffiliatesList";
import { useAffiliateKpiCards } from "../../queries/affiliates/useAffiliateKpiCards";
import { useAffiliatePromoCodes } from "../../queries/affiliates/useAffiliatePromoCodes";
import { useAffiliateListReportDownload } from "../../queries/affiliates/useAffiliateReportDownloads";
import type { OperatorAffiliateListItem } from "../../types/affiliates";
import { getAdminNow } from "../../utils/dateService";

type AffiliatesTab = "kpis" | "list";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

type DateRange = [Dayjs, Dayjs];

// `DatePicker` is still plain JS — cast until it gets TS types. The
// header date picker drives the list query (`listDates`) and only shows
// when the list tab is active; the KPIs tab owns its own picker through
// `AffiliateKpis`.
type DatePickerComponentProps = {
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  getData: () => void;
  showQuickSelect?: boolean;
  showPresets?: boolean;
  startRange?: string;
};
const TypedDatePicker = DatePicker as unknown as FC<DatePickerComponentProps>;

const toAffiliateRow = (item: OperatorAffiliateListItem): AffiliateRow => {
  const metrics = mapKpiMetricsToRow(item.metrics);
  // Top-level plan_type can be stale on older rows
  const planType = item.current_plan?.plan_type ?? item.plan_type;
  metrics.cpaAmount =
    planType === "CPA"
      ? (item.current_plan?.cpa_payment_per_action ?? null)
      : null;
  return {
    key: item.affiliate_id,
    id: item.affiliate_id,
    name: item.contact_name || item.company_name || item.email || undefined,
    paymentModel: item.plan_type,
    promoCodeCount: item.promo_code_count,
    ...metrics,
    status: item.status === "active" ? "active" : "deactivated",
  };
};

const Affiliates: FC = () => {
  usePageTitle(AppStrings.AFFILIATES);

  // Page-level tab: KPIs (overview) is the default view; the table lives
  // behind the "Affiliate List" tab. Each tab owns its own date range
  // (`kpisDates` / `listDates`) — list filters, cursor state, and both
  // date ranges live in the parent, so a user can configure filters →
  // flip to KPIs → flip back without losing their place. The query
  // re-keys on tab change because the active tab feeds its own dates
  // (and only the list tab adds `status` / `search`).
  const [activeTab, setActiveTab] = useState<AffiliatesTab>("kpis");
  const [kpisDates, setKpisDates] = useState<DateRange>([
    getAdminNow().startOf("month"),
    getAdminNow(),
  ]);
  const [listDates, setListDates] = useState<DateRange>([
    getAdminNow().startOf("month"),
    getAdminNow(),
  ]);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    AffiliateRow["status"] | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Cursor pagination — no jump-to-page-N. `cursorStack[N-1]` holds the
  // `last_evaluated_key` used to fetch page N (so `cursorStack[0]` is
  // always `undefined` for the first page).
  const [cursorStack, setCursorStack] = useState<(string | null | undefined)[]>(
    [undefined],
  );

  const debouncedSearch = useDebouncedValue(searchValue);

  // Pagination only applies on the list tab — when on KPIs the cursor is
  // forced to the first page so the response stays small (we only read
  // `portfolio_kpis` from it).
  const lastEvaluatedKey =
    activeTab !== "list" || currentPage <= 1
      ? undefined
      : (cursorStack[currentPage - 1] ?? undefined);

  const kpisStartDate = kpisDates[0].format("YYYY-MM-DD");
  const kpisEndDate = kpisDates[1].format("YYYY-MM-DD");
  const listStartDate = listDates[0].format("YYYY-MM-DD");
  const listEndDate = listDates[1].format("YYYY-MM-DD");

  const startDate = activeTab === "kpis" ? kpisStartDate : listStartDate;
  const endDate = activeTab === "kpis" ? kpisEndDate : listEndDate;

  const listQuery = useAffiliatesList({
    limit: pageSize,
    last_evaluated_key: lastEvaluatedKey ?? null,
    // `status` and `search` only apply to the list view — sending them on
    // the KPIs tab would skew the `portfolio_kpis` aggregate.
    status: activeTab === "list" ? statusFilter : undefined,
    search:
      activeTab === "list" ? (debouncedSearch || undefined) : undefined,
    start_date: startDate,
    end_date: endDate,
  });

  // KPIs-tab filter selections. Lifted here so the same picks feed both
  // the collapsible panel inside `AffiliateKpis` and the analytics hooks
  // (KPI cards + the four charts inside `AffiliateKpisOverview`).
  const [selectedAffiliate, setSelectedAffiliate] = useState<string | undefined>(
    undefined,
  );
  const [selectedCode, setSelectedCode] = useState<string | undefined>(
    undefined,
  );

  // Program-level KPI cards come from the dedicated analytics endpoint
  // (reporting metrics are no longer embedded on the list response).
  const kpisQuery = useAffiliateKpiCards(
    { start_date: kpisStartDate, end_date: kpisEndDate },
    { affiliate: selectedAffiliate, promo_code: selectedCode },
  );

  // Options catalog for the KPIs filter dropdown. The affiliate select is
  // capped at the first page (100 rows) — enough for the top-of-file
  // picker; if operators need to pick beyond that we'll swap in a
  // search-as-you-type lookup. The code select depends on the picked
  // affiliate; without one the option list stays empty (the Select is
  // also disabled downstream so the operator can't accidentally send a
  // stale code).
  const AFFILIATE_OPTIONS_LIMIT = 100;
  const affiliateOptionsQuery = useAffiliatesList({
    limit: AFFILIATE_OPTIONS_LIMIT,
    start_date: kpisStartDate,
    end_date: kpisEndDate,
  });
  const affiliateOptions = useMemo(
    () =>
      (affiliateOptionsQuery.data?.items ?? []).map((item) => ({
        value: item.affiliate_id,
        label:
          item.contact_name || item.company_name || item.email || item.affiliate_id,
      })),
    [affiliateOptionsQuery.data?.items],
  );

  const codeOptionsQuery = useAffiliatePromoCodes(selectedAffiliate, {
    limit: AFFILIATE_OPTIONS_LIMIT,
    start_date: kpisStartDate,
    end_date: kpisEndDate,
  });
  const codeOptions = useMemo(
    () =>
      (codeOptionsQuery.data?.items ?? []).map((code) => ({
        value: code.promo_code,
        label: code.promo_code,
      })),
    [codeOptionsQuery.data?.items],
  );

  // Clear the selected code the moment the affiliate changes — the code
  // list is scoped to the affiliate, so a leftover selection would be a
  // stale filter on the next fetch.
  useEffect(() => {
    setSelectedCode(undefined);
  }, [selectedAffiliate]);

  const listDownload = useAffiliateListReportDownload();

  const affiliatesData = useMemo<AffiliateRow[]>(
    () => (listQuery.data?.items ?? []).map(toAffiliateRow),
    [listQuery.data?.items],
  );

  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.PROMOTIONS, "AFFILIATES");
  }, [setActiveSidebarTab]);

  // The KPIs tab exposes an orange filter button in the tab-strip row that
  // reveals the Affiliates + Code selects (the panel is rendered by
  // `AffiliateKpis`). State lives here because the toggle button sits next
  // to the tabs, but the panel it controls is tab-scoped content.
  const [showKpisFilters, setShowKpisFilters] = useState(false);

  // Server-side CSV export, scoped to each tab's filters:
  //   • KPIs tab: just the date range — status / search are list-only.
  //   • List tab: full filter set (status + debounced search + dates).
  // Both share the same mutation hook so `isPending` toggles for whichever
  // button the operator clicked.
  const handleKpisDownload = () =>
    listDownload.mutate({
      start_date: kpisStartDate,
      end_date: kpisEndDate,
    });

  const handleListDownload = () =>
    listDownload.mutate({
      status: statusFilter,
      search: debouncedSearch || undefined,
      start_date: listStartDate,
      end_date: listEndDate,
    });

  // Reset cursor stack + page index when LIST-tab filters change — a
  // stored `last_evaluated_key` only makes sense within the same filter
  // scope. KPIs-tab date changes don't affect pagination.
  useEffect(() => {
    setCurrentPage(1);
    setCursorStack([undefined]);
  }, [statusFilter, debouncedSearch, pageSize, listStartDate, listEndDate]);

  // Cursor stack only tracks the list-tab query. While the KPIs tab is
  // active the same hook returns a different query (different params), so
  // we ignore its `last_evaluated_key` to keep the stack list-scoped.
  useEffect(() => {
    if (activeTab !== "list") return;
    if (listQuery.data == null) return;
    const key = listQuery.data.last_evaluated_key ?? null;
    setCursorStack((prev) => {
      const next = [...prev];
      while (next.length <= currentPage) next.push(undefined);
      next[currentPage] = key;
      return next;
    });
  }, [activeTab, currentPage, listQuery.data]);

  // Cursor pagination doesn't return a total — fake one for the pager:
  // bump by one when there's a next cursor so "Next" stays enabled.
  const fakeTotal = useMemo(() => {
    const itemsThisPage = listQuery.data?.items.length ?? 0;
    const hasNext = Boolean(listQuery.data?.last_evaluated_key);
    return hasNext
      ? currentPage * pageSize + 1
      : (currentPage - 1) * pageSize + itemsThisPage;
  }, [listQuery.data, currentPage, pageSize]);

  const tabItems = useMemo<ReadonlyArray<{ id: AffiliatesTab; label: string }>>(
    () => [
      { id: "kpis", label: AppStrings.AFFILIATE_KPIS },
      { id: "list", label: AppStrings.AFFILIATE_LIST },
    ],
    [],
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          {/*
            Header controls (title row) mirror the same layout on both tabs
            so operators don't hunt for the date picker + download when
            flipping between KPIs and List. The tab-scoped filter dropdown
            (KPIs only) lives in the tab-strip row below.
          */}
          {activeTab === "kpis" && (
            <div
              className={styles.headerControls}
              data-testid="affiliates-kpis-header-controls"
            >
              <TypedDatePicker
                dates={kpisDates}
                setDates={setKpisDates}
                getData={() => {}}
                showQuickSelect
                showPresets
                startRange={AppStrings.THIS_MONTH}
              />
              <Button
                intent="neutral"
                iconLeft={<DownloadOutlined />}
                aria-label={AppStrings.DOWNLOAD}
                loading={listDownload.isPending}
                onClick={handleKpisDownload}
              />
            </div>
          )}
          {activeTab === "list" && (
            <div
              className={styles.headerControls}
              data-testid="affiliates-list-header-controls"
            >
              <TypedDatePicker
                dates={listDates}
                setDates={setListDates}
                getData={() => {}}
                showQuickSelect
                showPresets
                startRange={AppStrings.THIS_MONTH}
              />
              <Button
                intent="neutral"
                iconLeft={<DownloadOutlined />}
                aria-label={AppStrings.DOWNLOAD}
                loading={listDownload.isPending}
                onClick={handleListDownload}
              />
            </div>
          )}
        </div>

        <div className={styles.tabsRow}>
          <PillTabs<AffiliatesTab>
            items={tabItems}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel={AppStrings.AFFILIATES}
            data-testid="affiliates-tabs"
          />

          {activeTab === "kpis" && (
            <Button
              variant="solid"
              intent="primary"
              iconLeft={<Filter size={16} />}
              aria-label={AppStrings.AFFILIATE_KPIS_FILTERS}
              aria-expanded={showKpisFilters}
              onClick={() => setShowKpisFilters((prev) => !prev)}
              data-testid="affiliate-kpis-filters-toggle"
            >
              {showKpisFilters ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </Button>
          )}
        </div>

        {activeTab === "kpis" && (
          <AffiliateKpis
            kpis={kpisQuery.data}
            isLoading={kpisQuery.isFetching}
            showFilters={showKpisFilters}
            selectedAffiliate={selectedAffiliate}
            onAffiliateChange={setSelectedAffiliate}
            affiliateOptions={affiliateOptions}
            isLoadingAffiliates={affiliateOptionsQuery.isFetching}
            selectedCode={selectedCode}
            onCodeChange={setSelectedCode}
            codeOptions={codeOptions}
            isLoadingCodes={codeOptionsQuery.isFetching}
          />
        )}

        {activeTab === "list" && (
          <AffiliatesList
            rows={affiliatesData}
            loading={listQuery.isLoading || listQuery.isFetching}
            filters={{
              search: searchValue,
              onSearchChange: setSearchValue,
              status: statusFilter,
              onStatusChange: setStatusFilter,
            }}
            pagination={{
              total: fakeTotal,
              pageSize,
              currentPage,
              onPageSizeChange: (value: number) => {
                setCurrentPage(1);
                setPageSize(value);
              },
              // Forward step is gated by the presence of a `last_evaluated_key`
              // on the current response — cursor pagination has no concept of
              // "page N" so we never jump beyond the page we have a cursor for.
              onPageChange: (nextPage: number) => {
                if (nextPage < currentPage) {
                  setCurrentPage(nextPage);
                  return;
                }
                if (nextPage === currentPage) return;
                if (listQuery.data?.last_evaluated_key) {
                  setCurrentPage(nextPage);
                }
              },
            }}
          />
        )}
      </div>
    </>
  );
};

export default Affiliates;
